import { useState, useMemo, useEffect, useCallback, CSSProperties } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { GanttData, GanttPipeline } from '@/types/api'

// ─── Local types ──────────────────────────────────────────────────────────────

type ZoomKey = '1h' | '4h' | '12h' | '24h'
type StatusFilter = 'all' | 'failed' | 'running' | 'stuck'

interface ZoomRange {
  start: number   // minutes since midnight (local)
  end: number
  ticksEvery: number
}

interface ExpectedBand {
  scheduled: number  // minutes since midnight
  minMin: number     // min duration in minutes
  avgMin: number
  maxMin: number
}

interface ResolvedRun {
  startMin:    number
  endMin:      number | null    // null if still running
  durationMin: number | null
  status:      'success' | 'failed' | 'running' | 'stuck'
  startedAt:   string
  durationSec: number | null
}

interface EnrichedPipeline extends GanttPipeline {
  runs:        ResolvedRun[]
  bands:       ExpectedBand[]
  slots:       number[]
  todayFailed: number
  todayRunning: number
  todayStuck:  number
}

interface HoverState {
  pipeline: EnrichedPipeline
  run:      ResolvedRun
  x:        number
  y:        number
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ZOOM_RANGES: Record<ZoomKey, Pick<ZoomRange, 'ticksEvery'>> = {
  '24h': { ticksEvery: 120 },
  '12h': { ticksEvery: 60  },
  '4h':  { ticksEvery: 30  },
  '1h':  { ticksEvery: 15  },
}

const ZOOM_HALF_SPANS: Record<ZoomKey, number> = {
  '24h': 720,
  '12h': 360,
  '4h':  120,
  '1h':  30,
}

function getZoomRange(key: ZoomKey, nowMin: number): ZoomRange {
  const halfSpan = ZOOM_HALF_SPANS[key]
  return {
    start:      Math.max(0,    Math.floor(nowMin - halfSpan)),
    end:        Math.min(1440, Math.ceil(nowMin + halfSpan)),
    ticksEvery: ZOOM_RANGES[key].ticksEvery,
  }
}

function fmtTime(mins: number): string {
  const h = Math.floor(mins / 60) % 24
  const m = Math.floor(mins % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function fmtDur(mins: number): string {
  if (mins < 1)  return `${Math.round(mins * 60)}s`
  if (mins < 60) return `${Math.round(mins)}m`
  const h = Math.floor(mins / 60)
  const m = Math.round(mins % 60)
  return m ? `${h}h ${m}m` : `${h}h`
}

function isoToLocalMinutes(iso: string): number {
  // DB returns UTC datetimes without TZ suffix; append Z so JS parses as UTC
  const d = new Date(iso.replace(' ', 'T').replace(/Z?$/, 'Z'))
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60
}

function computeNowMinutes(): number {
  const d = new Date()
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60
}

function computeSlots(freq: string | null, details: string | null): number[] {
  if (!freq) return []
  const f = freq.toLowerCase().trim()

  const everyMin = f.match(/every\s+(\d+)\s*min/)
  if (everyMin) {
    const n = parseInt(everyMin[1])
    if (n > 0 && n <= 120) return Array.from({ length: Math.floor(1440 / n) }, (_, i) => i * n)
  }
  if (f === '15min' || f.includes('15 min')) return Array.from({ length: 96  }, (_, i) => i * 15)
  if (f === '30min' || f.includes('30 min')) return Array.from({ length: 48  }, (_, i) => i * 30)

  if (f === 'hourly' || f === 'every hour' || f === 'every 1 hour' || f === '1h') {
    return Array.from({ length: 24 }, (_, i) => i * 60 + 5)
  }

  const everyH = f.match(/every\s+(\d+)\s*h(?:our)?s?/)
  if (everyH) {
    const n = parseInt(everyH[1])
    if (n >= 2 && n <= 24) {
      const offset = n <= 2 ? 5 : n <= 4 ? 15 : 30
      return Array.from({ length: Math.floor(24 / n) }, (_, i) => i * n * 60 + offset)
    }
  }

  if (f.includes('twice') || f.includes('2x daily')) {
    const times = [...(details || '').matchAll(/(\d{1,2}):(\d{2})/g)]
      .map(m => parseInt(m[1]) * 60 + parseInt(m[2]))
    return times.length >= 2 ? times.slice(0, 2) : [6 * 60, 18 * 60]
  }

  if (f.includes('daily') || f.includes('once') || f.includes('nightly')) {
    const text = details ?? freq
    const tm = text.match(/(\d{1,2}):(\d{2})/)
    return tm ? [parseInt(tm[1]) * 60 + parseInt(tm[2])] : [6 * 60]
  }

  return []
}

function resolveRuns(pipeline: GanttPipeline, nowMin: number): ResolvedRun[] {
  const maxSec = pipeline.histMaxSec
  return pipeline.runsToday.map(r => {
    const startMin   = isoToLocalMinutes(r.startedAt)
    const durationMin = r.durationSec != null ? r.durationSec / 60 : null
    const endMin     = durationMin != null ? startMin + durationMin : null

    let status: ResolvedRun['status'] = (r.status === 'pending' || r.status === 'running') ? 'running' : r.status

    if (status === 'running' && maxSec != null) {
      const runningFor = nowMin - startMin
      if (runningFor > maxSec / 60 * 1.5 + 5) status = 'stuck'
    }

    return { startMin, endMin, durationMin, status, startedAt: r.startedAt, durationSec: r.durationSec }
  })
}

function computeBands(pipeline: GanttPipeline, slots: number[]): ExpectedBand[] {
  const { histMinSec, histAvgSec, histMaxSec } = pipeline
  if (histMinSec == null || histAvgSec == null || histMaxSec == null || slots.length === 0) return []
  return slots.map(s => ({
    scheduled: s,
    minMin:    histMinSec / 60,
    avgMin:    histAvgSec / 60,
    maxMin:    histMaxSec / 60,
  }))
}

function enrichPipeline(p: GanttPipeline, nowMin: number): EnrichedPipeline {
  const slots = computeSlots(p.scheduleFrequency, p.scheduleDetails)
  const runs  = resolveRuns(p, nowMin)
  const bands = computeBands(p, slots)
  return {
    ...p,
    slots, runs, bands,
    todayFailed:  runs.filter(r => r.status === 'failed').length,
    todayRunning: runs.filter(r => r.status === 'running').length,
    todayStuck:   runs.filter(r => r.status === 'stuck').length,
  }
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const STATUS = {
  success: { bar: '#2d7d7a', soft: '#cfe3e1', label: 'Success' },
  failed:  { bar: '#dc2626', soft: '#fee2e2', label: 'Failed'  },
  running: { bar: '#2563eb', soft: '#dbeafe', label: 'Running' },
  stuck:   { bar: '#dc2626', soft: '#fee2e2', label: 'Stuck'   },
} as const

const TEAL_SOFT    = 'oklch(0.72 0.052 198 / 0.2)'
const TEAL_BORDER  = 'oklch(0.72 0.052 198 / 0.4)'
const TEAL_AVG     = 'oklch(0.6 0.07 198)'
const NOW_COLOR    = 'oklch(0.55 0.22 25)'   // orange-red "now" line
const LABEL_W      = 200                     // px

// ─── Sub-components ───────────────────────────────────────────────────────────

function PipelineTrack({
  pipeline, range, nowMin, onHover,
}: {
  pipeline: EnrichedPipeline
  range: ZoomRange
  nowMin: number
  onHover: (h: HoverState | null) => void
}) {
  const span = range.end - range.start
  const xPct = (m: number) => `${Math.max(0, Math.min(100, ((m - range.start) / span) * 100))}%`
  const wPct = (a: number, b: number) => `${Math.max(0, Math.min(100, ((b - a) / span) * 100))}%`

  const visibleBands = pipeline.bands.filter(
    b => b.scheduled <= range.end && b.scheduled + b.maxMin >= range.start,
  )
  const visibleSlots = pipeline.slots.filter(s => s >= range.start && s <= range.end)
  const visibleRuns  = pipeline.runs.filter(
    r => r.startMin <= range.end && (r.endMin ?? nowMin) >= range.start,
  )

  const minDotSpan = span / 300

  return (
    <div style={{ position: 'relative', height: 36, borderBottom: '1px solid var(--ns-border)' }}>
      {/* Hour grid lines */}
      {Array.from({ length: Math.ceil(span / range.ticksEvery) + 1 }, (_, i) => {
        const t = range.start + i * range.ticksEvery
        if (t > range.end) return null
        return <div key={t} style={{ position: 'absolute', top: 0, bottom: 0, left: xPct(t), width: 1, background: 'var(--ns-border)', opacity: 0.6 }} />
      })}

      {/* Expected bands */}
      {visibleBands.map((b, i) => (
        <div key={`exp-${i}`}
          title={`Expected ${fmtDur(b.minMin)}–${fmtDur(b.maxMin)} (avg ${fmtDur(b.avgMin)})`}
          style={{
            position: 'absolute',
            left:   xPct(b.scheduled + b.minMin),
            width:  wPct(b.scheduled + b.minMin, b.scheduled + b.maxMin),
            height: 11, top: '50%', transform: 'translateY(-50%)',
            background: TEAL_SOFT, border: `1px solid ${TEAL_BORDER}`,
            borderRadius: 2, pointerEvents: 'none',
          }}
        >
          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: 2,
            left: `${b.maxMin > b.minMin ? ((b.avgMin - b.minMin) / (b.maxMin - b.minMin)) * 100 : 50}%`,
            background: TEAL_AVG,
          }} />
        </div>
      ))}

      {/* Scheduled carets */}
      {visibleSlots.map((s, i) => (
        <div key={`sched-${i}`}
          title={`Scheduled ${fmtTime(s)}`}
          style={{
            position: 'absolute', left: xPct(s), top: '50%',
            transform: 'translateX(-50%) translateY(calc(-50% + 8px))',
            width: 0, height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `5px solid ${TEAL_AVG}`,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Actual runs */}
      {visibleRuns.map((r, i) => {
        const col   = STATUS[r.status]
        const endM  = r.endMin ?? nowMin
        const isDot = (endM - r.startMin) < minDotSpan
        const runStyle: CSSProperties = {
          position: 'absolute',
          left: xPct(r.startMin),
          width: isDot ? 10 : wPct(r.startMin, endM),
          height: isDot ? 10 : 10,
          top: '50%',
          transform: isDot ? 'translateX(-50%) translateY(-50%)' : 'translateY(-50%)',
          background: col.bar,
          borderRadius: isDot ? '50%' : 3,
          cursor: 'pointer',
          zIndex: 2,
          overflow: 'hidden',
        }
        return (
          <div key={`run-${i}`} style={runStyle}
            onMouseEnter={ev => onHover({ pipeline, run: r, x: ev.clientX, y: ev.clientY })}
            onMouseMove={ev =>  onHover({ pipeline, run: r, x: ev.clientX, y: ev.clientY })}
            onMouseLeave={() => onHover(null)}
          >
            {r.status === 'running' && <div className="gantt-pulse" style={{ position: 'absolute', inset: 0, borderRadius: 3, background: col.bar }} />}
            {r.status === 'stuck'   && <div className="gantt-stripes" style={{ position: 'absolute', inset: 0 }} />}
          </div>
        )
      })}

      {/* Now-line */}
      {nowMin >= range.start && nowMin <= range.end && (
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: xPct(nowMin), width: 2, background: NOW_COLOR, pointerEvents: 'none', zIndex: 3 }} />
      )}
    </div>
  )
}

function TimeAxis({ range, nowMin }: { range: ZoomRange; nowMin: number }) {
  const span = range.end - range.start
  const ticks: number[] = []
  for (let t = range.start; t <= range.end; t += range.ticksEvery) ticks.push(t)

  return (
    <div style={{ position: 'relative', height: 26, borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
      {ticks.map(t => (
        <div key={t} style={{ position: 'absolute', left: `${((t - range.start) / span) * 100}%`, top: 0, bottom: 4, transform: 'translateX(-50%)', display: 'flex', alignItems: 'flex-end' }}>
          <span style={{ fontSize: 10, color: 'var(--ns-text-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(t)}</span>
        </div>
      ))}
      {nowMin >= range.start && nowMin <= range.end && (
        <div style={{
          position: 'absolute',
          left: `${((nowMin - range.start) / span) * 100}%`,
          top: '50%', transform: 'translate(-50%, -50%)',
          background: NOW_COLOR, color: 'white',
          borderRadius: 4, padding: '1px 6px',
          fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', zIndex: 4,
        }}>
          Now · {fmtTime(nowMin)}
        </div>
      )}
    </div>
  )
}

function Tooltip({ hover, nowMin }: { hover: HoverState; nowMin: number }) {
  const { pipeline, run, x, y } = hover
  const col  = STATUS[run.status]
  const durLabel = run.endMin != null
    ? fmtDur(run.endMin - run.startMin)
    : `${fmtDur(nowMin - run.startMin)} (so far)`

  // Find the closest scheduled slot to this run's start
  const closestSlot = pipeline.slots.reduce<number | null>((best: number | null, s: number) => {
    if (best === null) return s
    return Math.abs(s - run.startMin) < Math.abs(best - run.startMin) ? s : best
  }, null)

  const vpW = window.innerWidth
  const vpH = window.innerHeight
  const W   = 230
  const H   = 160
  const left = x + 14 + W > vpW ? x - W - 4 : x + 14
  const top  = y + 14 + H > vpH ? y - H - 4 : y + 14

  return (
    <div style={{
      position: 'fixed', left, top, zIndex: 1000,
      background: 'var(--ns-surface)',
      border: '1px solid var(--ns-border)',
      borderRadius: 8, padding: '10px 12px',
      boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
      pointerEvents: 'none', minWidth: W, fontSize: 12,
    }}>
      <div className="mono" style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 2 }}>{pipeline.pipelineName}</div>
      <div style={{ color: 'var(--ns-text-muted)', fontSize: 11, marginBottom: 8 }}>
        {pipeline.customerName} · {pipeline.workspaceName}
      </div>
      <div style={{ marginBottom: 8 }}>
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
          fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
          background: col.soft, color: col.bar,
        }}>{col.label}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '3px 12px', fontSize: 11 }}>
        {closestSlot != null && <>
          <span style={{ color: 'var(--ns-text-muted)' }}>Scheduled</span>
          <b style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmtTime(closestSlot)}</b>
        </>}
        <span style={{ color: 'var(--ns-text-muted)' }}>Started</span>
        <b style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmtTime(run.startMin)}</b>
        <span style={{ color: 'var(--ns-text-muted)' }}>{run.endMin != null ? 'Ended' : 'Running since'}</span>
        <b style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{run.endMin != null ? fmtTime(run.endMin) : '—'}</b>
        <span style={{ color: 'var(--ns-text-muted)' }}>Duration</span>
        <b style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{durLabel}</b>
      </div>
    </div>
  )
}

function Legend() {
  const items: { color: string; label: string; extra?: 'pulse' | 'stripes' | 'now' }[] = [
    { color: TEAL_SOFT,            label: 'Expected window (min · avg · max)' },
    { color: STATUS.success.bar,   label: 'Success'  },
    { color: STATUS.failed.bar,    label: 'Failed'   },
    { color: STATUS.running.bar,   label: 'Running', extra: 'pulse'   },
    { color: STATUS.stuck.bar,     label: 'Stuck',   extra: 'stripes' },
    { color: NOW_COLOR,            label: 'Now',     extra: 'now'     },
  ]
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '4px 16px',
      padding: '8px 12px', borderTop: '1px solid var(--ns-border)',
      fontSize: 11, color: 'var(--ns-text-muted)', background: 'var(--ns-surface-2)',
      flexShrink: 0,
    }}>
      {items.map(({ color, label, extra }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {extra === 'now'
            ? <div style={{ width: 2, height: 14, background: color }} />
            : extra === 'stripes'
            ? <div className="gantt-stripes" style={{ width: 20, height: 8, borderRadius: 2 }} />
            : extra === 'pulse'
            ? <div style={{ width: 20, height: 8, borderRadius: 2, background: color, position: 'relative', overflow: 'hidden' }}>
                <div className="gantt-pulse" style={{ position: 'absolute', inset: 0, background: color, borderRadius: 2 }} />
              </div>
            : label.includes('Expected')
            ? <div style={{ width: 20, height: 8, borderRadius: 2, background: TEAL_SOFT, border: `1px solid ${TEAL_BORDER}` }} />
            : <div style={{ width: 20, height: 8, borderRadius: 2, background: color }} />
          }
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

export default function PipelineScheduleTab() {
  const queryClient = useQueryClient()
  const [zoom,             setZoom]          = useState<ZoomKey>('12h')
  const [statusFilter,     setStatusFilter]  = useState<StatusFilter>('all')
  const [selectedCustomer, setCustomer]      = useState<string>('all')
  const [search,           setSearch]        = useState('')
  const [showBands,        setShowBands]     = useState(true)
  const [hover,            setHover]         = useState<HoverState | null>(null)
  const [nowMin,           setNowMin]        = useState(computeNowMinutes)

  useEffect(() => {
    const id = setInterval(() => setNowMin(computeNowMinutes()), 60_000)
    return () => clearInterval(id)
  }, [])

  const range = useMemo(() => getZoomRange(zoom, nowMin), [zoom, nowMin])

  const { data, isLoading } = useQuery({
    queryKey: ['monitoring', 'pipeline-schedule'],
    queryFn:  () => api.get<GanttData>('/monitoring/pipeline-schedule'),
    refetchInterval: 60_000,
  })

  const enriched = useMemo<EnrichedPipeline[]>(
    () => (data?.pipelines ?? []).map(p => enrichPipeline(p, nowMin)),
    [data, nowMin],
  )

  // Customer sidebar data
  const customers = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number; failed: number; stuck: number }>()
    for (const p of enriched) {
      const id = p.customerId
      if (!map.has(id)) map.set(id, { id, name: p.customerName ?? id, count: 0, failed: 0, stuck: 0 })
      const c = map.get(id)!
      c.count++
      c.failed += p.todayFailed
      c.stuck  += p.todayStuck
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [enriched])

  // Filtered pipelines
  const filtered = useMemo(() => {
    return enriched.filter(p => {
      if (selectedCustomer !== 'all' && p.customerId !== selectedCustomer) return false
      if (search && !p.pipelineName.toLowerCase().includes(search.toLowerCase())
                 && !p.workspaceName.toLowerCase().includes(search.toLowerCase())) return false
      if (statusFilter === 'failed'  && p.todayFailed  === 0) return false
      if (statusFilter === 'running' && p.todayRunning === 0) return false
      if (statusFilter === 'stuck'   && p.todayStuck   === 0) return false
      return true
    })
  }, [enriched, selectedCustomer, search, statusFilter])

  // Group by customer → workspace
  const grouped = useMemo(() => {
    const out: Record<string, { name: string; workspaces: Record<string, EnrichedPipeline[]> }> = {}
    for (const p of filtered) {
      const cid = p.customerId
      if (!out[cid]) out[cid] = { name: p.customerName ?? cid, workspaces: {} }
      if (!out[cid].workspaces[p.workspaceName]) out[cid].workspaces[p.workspaceName] = []
      out[cid].workspaces[p.workspaceName].push(p)
    }
    return out
  }, [filtered])

  // Filter pill counts
  const counts = useMemo(() => {
    const c = { failed: 0, running: 0, stuck: 0 }
    for (const p of enriched) {
      if (p.todayFailed  > 0) c.failed++
      if (p.todayRunning > 0) c.running++
      if (p.todayStuck   > 0) c.stuck++
    }
    return c
  }, [enriched])

  const handleHover = useCallback((h: HoverState | null) => setHover(h), [])

  if (isLoading) {
    return <div className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>Loading…</div>
  }

  return (
    <>
      {/* Keyframe animations */}
      <style>{`
        @keyframes _gantt_pulse {
          0%, 100% { opacity: 0.35; transform: scaleX(1); }
          50%       { opacity: 0.7;  transform: scaleX(1.03); }
        }
        @keyframes _gantt_stripes {
          from { background-position: 0 0; }
          to   { background-position: 20px 0; }
        }
        .gantt-pulse {
          animation: _gantt_pulse 1.5s ease-in-out infinite;
        }
        .gantt-stripes {
          background: repeating-linear-gradient(
            45deg,
            transparent,    transparent 3px,
            rgba(0,0,0,0.18) 3px, rgba(0,0,0,0.18) 6px
          );
          animation: _gantt_stripes 0.9s linear infinite;
        }
      `}</style>

      {/* Toolbar */}
      <div className="toolbar" style={{ marginBottom: 8 }}>
        {/* Search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ position: 'absolute', left: 8, color: 'var(--ns-text-muted)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text" className="search-box"
            placeholder="Search pipelines…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 28, width: 200 }}
          />
        </div>

        {/* Status filter pills */}
        <button
          className={`chip ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All <span className="count">{enriched.length}</span>
        </button>
        <button
          className={`chip ${statusFilter === 'failed' ? 'active' : ''}`}
          onClick={() => setStatusFilter(f => f === 'failed' ? 'all' : 'failed')}
        >
          <span className="dot dot-err" />Failed {counts.failed > 0 && <span className="count">{counts.failed}</span>}
        </button>
        <button
          className={`chip ${statusFilter === 'running' ? 'active' : ''}`}
          onClick={() => setStatusFilter(f => f === 'running' ? 'all' : 'running')}
        >
          <span className="dot" style={{ background: 'var(--ns-info)' }} />Running {counts.running > 0 && <span className="count">{counts.running}</span>}
        </button>
        {counts.stuck > 0 && (
          <button
            className={`chip ${statusFilter === 'stuck' ? 'active' : ''}`}
            onClick={() => setStatusFilter(f => f === 'stuck' ? 'all' : 'stuck')}
          >
            <span className="dot dot-err" />Stuck <span className="count">{counts.stuck}</span>
          </button>
        )}

        <div className="spacer" />

        {/* Expected bands toggle */}
        <button className="btn btn-ghost btn-sm" onClick={() => setShowBands(v => !v)}>
          <span style={{
            display: 'inline-block', width: 28, height: 16, borderRadius: 999,
            background: showBands ? 'var(--ns-brand-strong)' : 'var(--ns-border-strong)',
            position: 'relative', transition: 'background 0.15s', flexShrink: 0,
          }}>
            <span style={{
              position: 'absolute', top: 2, left: showBands ? 14 : 2,
              width: 12, height: 12, borderRadius: '50%', background: 'white',
              transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }} />
          </span>
          Expected
        </button>

        {/* Refresh */}
        <button className="btn btn-ghost btn-sm" onClick={() => queryClient.invalidateQueries({ queryKey: ['monitoring', 'pipeline-schedule'] })}>
          <RefreshCw size={13} />
        </button>

        {/* Zoom */}
        <div className="seg">
          {(['1h', '4h', '12h', '24h'] as ZoomKey[]).map(k => (
            <button key={k} className={zoom === k ? 'active' : ''} onClick={() => setZoom(k)}>
              {k}
            </button>
          ))}
        </div>
      </div>

      {/* Grid: sidebar + gantt */}
      <div style={{
        display: 'flex', border: '1px solid var(--ns-border)', borderRadius: 8,
        overflow: 'hidden', height: 'calc(100vh - 310px)', minHeight: 360,
        background: 'var(--ns-surface)',
      }}>

        {/* Customer sidebar */}
        <aside style={{
          width: 178, flexShrink: 0,
          borderRight: '1px solid var(--ns-border)',
          background: 'var(--ns-surface-2)',
          overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
            color: 'var(--ns-text-muted)', padding: '10px 12px 6px',
          }}>
            Customers
          </div>

          {/* "All customers" row */}
          <button
            onClick={() => setCustomer('all')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
              padding: '6px 10px', textAlign: 'left', border: 0,
              background: selectedCustomer === 'all' ? 'oklch(0.72 0.052 198 / 0.12)' : 'transparent',
              borderLeft: `2px solid ${selectedCustomer === 'all' ? 'oklch(0.72 0.052 198)' : 'transparent'}`,
              cursor: 'pointer',
            }}
          >
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 500 }}>All customers</span>
            <span style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>{enriched.length}</span>
          </button>

          {customers.map(c => {
            const active = selectedCustomer === c.id
            const dot = c.failed > 0 ? 'dot-err' : c.stuck > 0 ? 'dot-warn' : 'dot-ok'
            return (
              <button
                key={c.id}
                onClick={() => setCustomer(c.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                  padding: '6px 10px', textAlign: 'left', border: 0,
                  background: active ? 'oklch(0.72 0.052 198 / 0.12)' : 'transparent',
                  borderLeft: `2px solid ${active ? 'oklch(0.72 0.052 198)' : 'transparent'}`,
                  cursor: 'pointer',
                }}
              >
                <span className={`dot ${dot}`} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name}
                </span>
                <span style={{ fontSize: 11, color: 'var(--ns-text-muted)', flexShrink: 0 }}>{c.count}</span>
                {c.failed > 0 && (
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: 'var(--ns-err-soft)', color: 'var(--ns-err)', flexShrink: 0 }}>
                    {c.failed}
                  </span>
                )}
              </button>
            )
          })}
        </aside>

        {/* Gantt area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* Header row: label column + time axis */}
          <div style={{ display: 'flex', flexShrink: 0 }}>
            <div style={{
              width: LABEL_W, flexShrink: 0,
              padding: '0 8px', display: 'flex', alignItems: 'center',
              borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)',
            }}>
              <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ns-text-muted)' }}>
                Pipeline
              </span>
            </div>
            <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
              <TimeAxis range={range} nowMin={nowMin} />
            </div>
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {Object.entries(grouped).map(([cid, group]) => (
              <div key={cid}>
                {/* Customer group header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px',
                  background: 'var(--ns-surface-2)',
                  borderBottom: '1px solid var(--ns-border)',
                  position: 'sticky', top: 0, zIndex: 5,
                }}>
                  <span style={{ fontWeight: 700, fontSize: 12.5 }}>{group.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>
                    {Object.values(group.workspaces).flat().length} pipelines
                  </span>
                </div>

                {/* Workspaces */}
                {Object.entries(group.workspaces).map(([ws, pipes]) => (
                  <div key={ws}>
                    {/* Workspace sub-header */}
                    <div style={{
                      display: 'flex',
                      borderBottom: '1px solid var(--ns-border)',
                      background: 'oklch(from var(--ns-surface-2) calc(l - 0.01) c h)',
                    }}>
                      <div style={{ width: LABEL_W, flexShrink: 0, padding: '4px 8px 4px 16px' }}>
                        <span className="mono muted" style={{ fontSize: 11 }}>{ws}</span>
                      </div>
                      <div style={{ flex: 1 }} />
                    </div>

                    {/* Pipeline rows */}
                    {pipes.map(p => (
                      <div key={p.pipelineName} style={{ display: 'flex' }}>
                        {/* Label */}
                        <div style={{
                          width: LABEL_W, flexShrink: 0,
                          padding: '0 6px 0 20px', display: 'flex', flexDirection: 'column',
                          justifyContent: 'center',
                          borderBottom: '1px solid var(--ns-border)',
                          minHeight: 36,
                        }}>
                          <div className="mono" style={{
                            fontSize: 11.5, fontWeight: 500,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            color: p.todayFailed > 0 || p.todayStuck > 0 ? 'var(--ns-err)' : 'var(--ns-text)',
                          }} title={p.pipelineName}>
                            {p.pipelineName}
                          </div>
                          <div style={{ fontSize: 9.5, color: 'var(--ns-text-muted)', display: 'flex', gap: 3, flexWrap: 'nowrap', overflow: 'hidden' }}>
                            {p.scheduleFrequency && <span>{p.scheduleFrequency}</span>}
                            {p.histAvgSec != null && <><span>·</span><span>avg {fmtDur(p.histAvgSec / 60)}</span></>}
                            {p.todayFailed > 0 && <><span>·</span><span style={{ color: 'var(--ns-err)', fontWeight: 600 }}>{p.todayFailed} failed</span></>}
                            {p.todayStuck  > 0 && <><span>·</span><span style={{ color: 'var(--ns-err)', fontWeight: 600 }}>stuck</span></>}
                          </div>
                        </div>

                        {/* Track */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <PipelineTrack
                            pipeline={p}
                            range={range}
                            nowMin={nowMin}
                            onHover={handleHover}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="muted" style={{ textAlign: 'center', padding: 60, fontSize: 13 }}>
                {enriched.length === 0
                  ? 'No pipeline events in the monitoring database.'
                  : 'No pipelines match the current filters.'}
              </div>
            )}
          </div>

          <Legend />
        </div>
      </div>

      {hover && <Tooltip hover={hover} nowMin={nowMin} />}
    </>
  )
}
